/**
 * Centralized API URL resolution.
 *
 * When the browser is on a non-localhost host (e.g. 192.168.0.76 or ai.sys9.co),
 * we MUST use window.location.origin so requests go through the same host
 * instead of hitting a local-only backend URL (unreachable from remote browsers).
 *
 * Usage:
 *   import { getApiBaseUrl, getWsBaseUrl } from '../config/api';
 *   const apiBase = getApiBaseUrl();  // e.g. "https://ai.sys9.co"
 *   const wsBase = getWsBaseUrl();    // e.g. "wss://ai.sys9.co"
 */

function isRemoteBrowser(): boolean {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return !!host && host !== 'localhost' && host !== '127.0.0.1';
}

function readEnvApiBase(): string {
  const env =
    (typeof import.meta !== 'undefined' &&
      import.meta.env?.VITE_API_BASE_URL) ||
    (typeof process !== 'undefined' && process.env?.REACT_APP_API_BASE_URL) ||
    '';
  return String(env).replace(/\/$/, '');
}

function readEnvWsBase(): string {
  const env =
    (typeof import.meta !== 'undefined' && import.meta.env?.VITE_WS_URL) ||
    (typeof process !== 'undefined' && process.env?.REACT_APP_WS_URL) ||
    '';
  return String(env).replace(/\/$/, '');
}

/**
 * Resolve the API base URL.
 *
 * Rules:
 * 1. If an explicit env var is set AND we are on localhost → use it.
 * 2. If we are on a remote host and the env var points to localhost
 *    → ignore it and derive from window.location.origin.
 * 3. Otherwise fall back to window.location.origin.
 */
export function getApiBaseUrl(): string {
  const env = readEnvApiBase();

  if (env) {
    // On remote hosts, never use a localhost env value
    if (isRemoteBrowser() && env.includes('localhost')) {
      return window.location.origin;
    }
    return env;
  }

  if (typeof window !== 'undefined' && window.location.origin) {
    return window.location.origin;
  }

  return 'http://localhost:8000';
}

/**
 * Resolve the WebSocket base URL.
 *
 * Rules mirror getApiBaseUrl() but use ws:// / wss:// scheme.
 */
export function getWsBaseUrl(): string {
  // VITE_WS_URL / REACT_APP_WS_URL conventionally hold the FULL main-service
  // socket URL (vite.config.ts polyfills REACT_APP_WS_URL to
  // `${apiBaseUrl}/api/v1/ws`). Callers of this function append their own
  // endpoint path, so returning that value verbatim composed double-path
  // URLs like ws://host/api/v1/ws/ws/hitl. Strip the well-known endpoint
  // suffix — but only that suffix, so a deliberate path prefix (a
  // path-routed proxy like wss://example.com/backend/api/v1/ws) survives
  // as wss://example.com/backend.
  const env = readEnvWsBase().replace(/\/api\/v1\/ws$/, '');

  if (env) {
    // On remote hosts, never use a localhost env value
    if (isRemoteBrowser() && env.includes('localhost')) {
      const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      return `${proto}//${window.location.host}`;
    }
    return env;
  }

  if (typeof window !== 'undefined') {
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${proto}//${window.location.host}`;
  }

  return 'ws://localhost:8000';
}

/**
 * Resolve a WebSocket URL, preserving its path but switching host
 * when running on a remote browser with a localhost env value.
 */
export function resolveWsUrl(url: string): string {
  if (typeof window === 'undefined') return url;
  if (!window.location.hostname) return url;
  if (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1'
  ) {
    return url;
  }
  if (url.includes('localhost') || url.includes('127.0.0.1')) {
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const pathMatch = url.match(/^wss?:\/\/[^/]+(.*)$/);
    const path = pathMatch ? pathMatch[1] : '';
    return `${proto}//${window.location.host}${path}`;
  }
  return url;
}

/**
 * Build an absolute URL from a path (or return as-is if already absolute).
 */
export function buildAbsoluteUrl(pathOrUrl: string): string {
  if (!pathOrUrl) return pathOrUrl;
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  const base = getApiBaseUrl().replace(/\/$/, '');
  const path = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
  return `${base}${path}`;
}
